/**
 * "Escolha o seu município" (D3; ORD-F2-022 PR "cidade", FRONT-2): busca no cadastro que o site já tem e guarda só o
 * código IBGE neste aparelho (actions.chooseMyCity). Com o município guardado, mostra o nome, o link para a rota dele (a
 * troca das 17h00 mantém a rota) e "Esquecer". Atrás de `features.my_city` (desligada; ligar é PARE, com o /privacidade
 * novo).
 *
 * Em `components/` desde o §72 de docs/DECISOES.md, para servir à página inicial e às duas pré-apurações sem import entre
 * features. Componente não lê `state/` (DE-13): quem o monta liga a chave, o município guardado, o cadastro e as ações
 * por `useMyCityPicker()` (state/hooks.ts), que devolve null sem a chave ou sem a geografia (aí o campo não existe).
 * Fica no JS crítico (regra dos 100 ms, §67): a pré-apuração o recebe pronto do `app/phaseScreen.tsx` (`MyCitySlot`),
 * sem importá-lo, para não nascer um pedaço comum entre os pedaços sob demanda.
 */
import { useState } from 'preact/hooks';
import { tMyCity } from '../copy/myCity';
import './MyCityPicker.css';

/** O que o campo usa de cada município do cadastro (`MunicipalityInfo`; tipado aqui, componente não importa contracts). */
export interface MyCityEntry {
  ibge: string;
  name: string;
  uf: string;
}

export interface MyCityPickerProps {
  /** Os municípios do cadastro (`geography.municipalities`). */
  cities: readonly MyCityEntry[];
  /** O código IBGE guardado no aparelho, ou null. */
  chosen: string | null;
  onChoose(ibge: string): void;
  onForget(): void;
}

const MAX = 6;
const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** Até 6 municípios: primeiro os que começam pelo texto, depois os que o contêm (ordem alfabética dentro de cada grupo). */
export function searchMunicipalities<T extends MyCityEntry>(list: readonly T[], query: string): T[] {
  const q = fold(query);
  if (q.length < 2) return [];
  const starts: T[] = [];
  const has: T[] = [];
  for (const m of list) {
    const n = fold(m.name);
    if (n.startsWith(q)) starts.push(m);
    else if (n.includes(q)) has.push(m);
  }
  const by = (a: T, b: T) => a.name.localeCompare(b.name, 'pt-BR') || a.uf.localeCompare(b.uf);
  return [...starts.sort(by), ...has.sort(by)].slice(0, MAX);
}

export function MyCityPicker({ cities, chosen, onChoose, onForget }: MyCityPickerProps) {
  const [q, setQ] = useState('');
  const mine = chosen ? cities.find((m) => m.ibge === chosen) : undefined;
  if (mine) {
    const name = `${mine.name} (${mine.uf})`;
    return (
      <div class="home-mycity" data-testid="home-mycity" data-state="chosen">
        <p class="home-mycity__chosen">{tMyCity('home.mycity.chosen', { CIDADE: name })}</p>
        <a
          class="home-mycity__go"
          href={`#/presidente/${mine.uf.toLowerCase()}/${mine.ibge}`}
          data-testid="home-mycity-go"
        >
          {tMyCity('home.mycity.go', { CIDADE: mine.name })}
        </a>
        <button type="button" class="home-mycity__forget" data-testid="home-mycity-forget" onClick={onForget}>
          {tMyCity('home.mycity.forget')}
        </button>
      </div>
    );
  }
  const found = searchMunicipalities(cities, q);
  return (
    <div class="home-mycity" data-testid="home-mycity" data-state="empty">
      <label class="home-mycity__label">
        <span class="home-mycity__title">{tMyCity('home.mycity.title')}</span>
        <input
          class="home-mycity__input"
          type="search"
          autocomplete="off"
          placeholder={tMyCity('home.mycity.placeholder')}
          value={q}
          data-testid="home-mycity-input"
          onInput={(e) => setQ((e.currentTarget as HTMLInputElement).value)}
        />
      </label>
      {found.length > 0 && (
        <ul class="home-mycity__list" data-testid="home-mycity-list">
          {found.map((m) => (
            <li key={m.ibge}>
              <button type="button" class="home-mycity__option" onClick={() => onChoose(m.ibge)}>
                {`${m.name} (${m.uf})`}
              </button>
            </li>
          ))}
        </ul>
      )}
      <p class="home-mycity__note">{tMyCity('home.mycity.note')}</p>
    </div>
  );
}
